import React, { useState, useCallback } from "react";
import { Button, Spinner, Badge } from "react-bootstrap";
import {
  LuArrowRight,
  LuShoppingBag,
  LuPackageCheck,
  LuTruck,
  LuPackage,
  LuCheck,
} from "react-icons/lu";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { OrdersToHandleProps, OrderTab } from "../types";
import {
  isNewOrder,
  canMarkAsProcessing,
  canMarkAsReady,
  isReadyOrder,
  isInTransitOrder,
  canUpdateOrder,
  getDisplayOrderStatus,
  getCustomerName,
  getOrderStatusBadgeStyle,
} from "../utils";
import UpdateOrderModal from "../../OrderList/components/UpdateOrderModal";
import { Order } from "../../OrderList/types";
import { STORAGE_CONSTANTS } from "../../../shared/Constants/Constants";

const OrdersToHandle: React.FC<OrdersToHandleProps> = ({
  orders,
  ordersLoading,
  activeOrderTab,
  tabCounts,
  updatingOrderId,
  onTabChange,
  onUpdateStatus,
  onOrdersRefresh,
  onViewAllOrders,
}) => {
  const navigate = useNavigate();

  // State for UpdateOrderModal (same modal logic used in OrderList)
  const [selectedOrderForModal, setSelectedOrderForModal] =
    useState<Order | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);

  const handleOpenUpdateModal = useCallback((order: Order) => {
    setSelectedOrderForModal(order);
    setShowUpdateModal(true);
  }, []);

  const handleCloseUpdateModal = useCallback(() => {
    setSelectedOrderForModal(null);
    setShowUpdateModal(false);
  }, []);

  const handleModalOrderUpdated = useCallback(() => {
    handleCloseUpdateModal();
    if (onOrdersRefresh) {
      onOrdersRefresh();
    }
  }, [handleCloseUpdateModal, onOrdersRefresh]);

  const handleNavigateOrder = useCallback(
    (orderId: string) => {
      localStorage.setItem(STORAGE_CONSTANTS.ORDER_ID, orderId);
      navigate("/view-order");
    },
    [navigate]
  );

  return (
    <div className="bg-white rounded-4 border border-1 border-light-subtle p-4 h-100 shadow-sm d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold mb-0 text-dark fs-5">Orders to handle</h4>
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none text-primary small d-flex align-items-center gap-1"
          onClick={onViewAllOrders}
        >
          View all orders <LuArrowRight size={13} />
        </button>
      </div>

      {/* Order Filter Tabs */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {(
          [
            { key: "all", label: `All (${tabCounts.all})` },
            { key: "new", label: `New (${tabCounts.new})` },
            { key: "packing", label: `Packing (${tabCounts.packing})` },
            { key: "in_transit", label: `In transit (${tabCounts.in_transit})` },
            { key: "others", label: `Others (${tabCounts.others})` },
          ] as { key: OrderTab; label: string }[]
        ).map(({ key, label }) => (
          <Button
            key={key}
            size="sm"
            variant={activeOrderTab === key ? "primary" : "light"}
            className={`rounded-pill px-3 py-1 fw-semibold small ${
              activeOrderTab === key ? "" : "text-secondary border"
            }`}
            onClick={() => onTabChange(key)}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Order Items List */}
      <div className="d-flex flex-column gap-3 flex-grow-1">
        {ordersLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-5 text-secondary">
            <LuShoppingBag size={32} className="mb-2 text-muted" />
            <div>No orders to handle in this category</div>
          </div>
        ) : (
          orders.slice(0, 5).map((order) => {
            const isNew = isNewOrder(order);
            const isProcessing = canMarkAsReady(order);
            const isReady = isReadyOrder(order);
            const displayStatus = getDisplayOrderStatus(order);
            const badgeStyle = getOrderStatusBadgeStyle(displayStatus);
            const customerName = getCustomerName(order);

            return (
              <div
                key={order.id}
                className="p-3 rounded-4 border d-flex flex-wrap align-items-center justify-content-between gap-3"
                style={{ backgroundColor: "#FFFFFF" }}
              >
                {/* Order Item Left Details */}
                <div className="d-flex align-items-center gap-3 min-w-0">
                  {/* Status Icon */}
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 44,
                      height: 44,
                      backgroundColor: `${badgeStyle.backgroundColor}18`,
                      color: badgeStyle.backgroundColor,
                    }}
                  >
                    {isNew ? (
                      <LuShoppingBag size={20} />
                    ) : isReady || isProcessing ? (
                      <LuPackageCheck size={20} />
                    ) : isInTransitOrder(order) ? (
                      <LuTruck size={20} />
                    ) : (
                      <LuPackage size={20} />
                    )}
                  </div>

                  <div className="min-w-0">
                    {/* Customer Name & Status Badge */}
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                      <span className="fw-bold text-dark">
                        {customerName}
                      </span>

                      <Badge
                        className="px-2.5 py-1 fw-semibold rounded-pill text-white shadow-sm"
                        style={{
                          backgroundColor: badgeStyle.backgroundColor,
                          color: "#FFFFFF",
                          fontSize: "11px",
                          letterSpacing: "0.2px",
                        }}
                      >
                        {displayStatus}
                      </Badge>
                    </div>

                    {/* Subtitle details */}
                    <div className="text-secondary small">
                      <span
                        className="fw-medium text-primary text-decoration-underline"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleNavigateOrder(order.id)}
                        title="View order details"
                      >
                        #SL-{order.id}
                      </span>
                      <span className="mx-1.5">•</span>
                      <span>{order.order_details?.length || 1} items</span>
                      <span className="mx-1.5">•</span>
                      <span className="fw-semibold text-dark">
                        Rs. {Number(order.total_amount || 0).toLocaleString()}
                      </span>
                      <span className="mx-1.5">•</span>
                      <span className="text-muted">
                        {order.purchase_date
                          ? moment(order.purchase_date).fromNow()
                          : "Recently"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Order Item Right Action Buttons */}
                <div className="flex-shrink-0 ms-auto d-flex align-items-center gap-2">
                  {canMarkAsProcessing(order) ? (
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="rounded-3 d-inline-flex align-items-center gap-1.5 fw-semibold px-3 py-1.5"
                      disabled={updatingOrderId === order.id}
                      onClick={() => onUpdateStatus(order, "PROCESSING")}
                    >
                      {updatingOrderId === order.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <>
                          <LuCheck size={14} /> Mark as Processing
                        </>
                      )}
                    </Button>
                  ) : canMarkAsReady(order) ? (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="rounded-3 d-inline-flex align-items-center gap-1.5 fw-semibold px-3 py-1.5 text-warning-emphasis"
                      disabled={updatingOrderId === order.id}
                      onClick={() => onUpdateStatus(order, "READY")}
                    >
                      {updatingOrderId === order.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <>
                          <LuCheck size={14} /> Mark ready
                        </>
                      )}
                    </Button>
                  ) : canUpdateOrder(order) ? (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="rounded-3 d-inline-flex align-items-center gap-1.5 fw-semibold px-3 py-1.5"
                      onClick={() => handleOpenUpdateModal(order)}
                    >
                      Update Order
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Update Order Modal (same as OrderList) */}
      <UpdateOrderModal
        show={showUpdateModal}
        onHide={handleCloseUpdateModal}
        orderId={selectedOrderForModal?.id ?? null}
        currentStatus={
          selectedOrderForModal
            ? selectedOrderForModal.store_order_status?.status ||
              selectedOrderForModal.status
            : null
        }
        deliveryMethod={selectedOrderForModal?.delivery_method ?? "DELIVERY"}
        onOrderUpdated={handleModalOrderUpdated}
      />
    </div>
  );
};

export default React.memo(OrdersToHandle);
